import React from 'react';
import PropTypes from 'prop-types';
import { Paper, TextField, MenuItem, Button, Tabs, Tab } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { TAB_INDICES } from '../constants';

const useStyles = makeStyles(theme => ({
  filtersPanel: {
    background: 'transparent',
    border: 'none',
    borderRadius: 0,
    padding: theme.spacing(0.5, 0, 0.5),
    marginBottom: theme.spacing(1.5),
    boxShadow: 'none',
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(1.2),
    flexWrap: 'nowrap',
    minHeight: 48,
    overflowX: 'auto',
    overflowY: 'hidden'
  },
  tabsBarWrap: {
    display: 'flex',
    justifyContent: 'center',
    border: 'none',
    borderRadius: 0,
    padding: 0,
    background: 'transparent',
    boxShadow: 'none',
    flexShrink: 0
  },
  tabsStyled: {
    minHeight: 40,
    '& .MuiTabs-indicator': { display: 'none' },
    '& .MuiTab-root': {
      textTransform: 'none',
      fontWeight: 500,
      minHeight: 36,
      borderRadius: 12,
      margin: theme.spacing(0, .5),
      padding: theme.spacing(0.5, 2),
      transition: 'background .25s',
      '&.Mui-selected': {
        background: '#1a73e8',
        color: '#fff'
      }
    }
  },
  filtersFieldsWrap: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(1),
    flex: 1,
    justifyContent: 'flex-end',
    flexWrap: 'nowrap'
  },
  filterField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      background: '#fff'
    }
  },
  filterActions: { 
    display: 'flex', 
    gap: theme.spacing(1), 
    alignItems: 'flex-end', 
    marginLeft: 'auto' 
  },
  smallBtn: { 
    textTransform: 'none', 
    borderRadius: 12, 
    padding: '8px 18px', 
    fontWeight: 500 
  }
}));

/**
 * Barre combinée des onglets et filtres
 */
const FiltersBar = ({
  tab,
  onTabChange,
  filters,
  tempDateDebut,
  tempDateFin,
  onTempDateDebutChange,
  onTempDateFinChange,
  onDateDebutBlur,
  onDateFinBlur,
  onDateDebutKeyDown,
  onDateFinKeyDown,
  onFilterChange,
  onResetFilters,
  fournisseurs,
  clients,
  metaux,
  filtersRef,
  totalPrixAchat
}) => {
  const classes = useStyles();

  return (
    <Paper className={classes.filtersPanel} ref={filtersRef} style={{ marginBottom: 24 }}>
      <div className={classes.tabsBarWrap}>
        <Tabs 
          value={tab} 
          onChange={onTabChange} 
          className={classes.tabsStyled} 
          indicatorColor="primary" 
          textColor="primary" 
          variant="standard"
        >
          <Tab label="CLAL" />
          <Tab label="FOURNISSEURS" />
          <Tab label="CLIENTS" />
        </Tabs>
      </div>
      
      <div className={classes.filtersFieldsWrap}>
        <TextField
          className={classes.filterField}
          label="Date début"
          type="date"
          value={tempDateDebut}
          onChange={onTempDateDebutChange}
          onBlur={onDateDebutBlur}
          onKeyDown={onDateDebutKeyDown}
          variant="outlined"
          size="small"
          InputLabelProps={{ shrink: true }}
          style={{ width: 150, flex: '0 0 auto' }}
        />
        
        <TextField
          className={classes.filterField}
          label="Date fin"
          type="date"
          value={tempDateFin}
          onChange={onTempDateFinChange}
          onBlur={onDateFinBlur}
          onKeyDown={onDateFinKeyDown}
          variant="outlined"
          size="small"
          InputLabelProps={{ shrink: true }}
          style={{ width: 150, flex: '0 0 auto' }}
        />
        
        <div style={{ 
          width: (tab === TAB_INDICES.FOURNISSEURS || tab === TAB_INDICES.CLIENTS) ? 170 : 0, 
          flex: '0 0 auto', 
          overflow: 'hidden', 
          transition: 'width .25s' 
        }}>
          {tab === TAB_INDICES.FOURNISSEURS ? (
            <TextField
              className={classes.filterField}
              select
              label="Fournisseur"
              value={filters.fournisseurId}
              onChange={e => onFilterChange('fournisseurId', e.target?.value || '')}
              variant="outlined"
              size="small"
              fullWidth
            >
              <MenuItem value="">Tous</MenuItem>
              {fournisseurs.map((f, idx) => {
                const fid = f.id ?? `idx-${idx}`;
                return <MenuItem key={`four-${fid}`} value={fid}>{f.label}</MenuItem>;
              })}
            </TextField>
          ) : tab === TAB_INDICES.CLIENTS ? (
            <TextField
              className={classes.filterField}
              select
              label="Client"
              value={filters.clientId}
              onChange={e => onFilterChange('clientId', e.target?.value || '')}
              variant="outlined"
              size="small"
              fullWidth
            >
              <MenuItem value="">Tous</MenuItem>
              {clients.map((c, idx) => {
                const cid = c.id ?? `idx-${idx}`;
                return <MenuItem key={`cli-${cid}`} value={cid}>{c.label}</MenuItem>;
              })}
            </TextField>
          ) : null}
        </div>
        
        <TextField
          className={classes.filterField}
          select
          label="Métal"
          value={filters.metalId}
          onChange={e => onFilterChange('metalId', e.target?.value || '')}
          variant="outlined"
          size="small"
          style={{ width: 120, flex: '0 0 auto' }}
        >
          <MenuItem value="">Tous</MenuItem>
          {metaux.map(m => (
            <MenuItem key={`metal-${m.id_metal}`} value={m.id_metal}>
              {m.description}
            </MenuItem>
          ))}
        </TextField>

        {tab === TAB_INDICES.CLAL && (
          <TextField
            className={classes.filterField}
            select
            label="Achat"
            value={filters.achat}
            onChange={e => onFilterChange('achat', e.target?.value || '')}
            variant="outlined"
            size="small"
            style={{ width: 130, flex: '0 0 auto' }}
          >
            <MenuItem value="">Tous</MenuItem>
            <MenuItem value="1">Achat</MenuItem>
            <MenuItem value="0">Hors achat</MenuItem>
          </TextField>
        )}


        
        <TextField
          className={classes.filterField}
          label="Désignation"
          value={filters.designation}
          onChange={e => onFilterChange('designation', e.target?.value || '')}
          variant="outlined"
          size="small"
          placeholder="Filtrer..."
          style={{ width: 160, flex: '0 0 auto' }}
        />
        
        <div className={classes.filterActions} style={{ flex: '0 0 auto', marginLeft: 8 }}>
          <Button 
            className={classes.smallBtn} 
            variant="outlined" 
            size="small" 
            onClick={onResetFilters}
          >
            Réinit.
          </Button>
        </div>
      </div>
    </Paper>
  );
};

FiltersBar.propTypes = {
  tab: PropTypes.number.isRequired,
  onTabChange: PropTypes.func.isRequired,
  filters: PropTypes.object.isRequired,
  tempDateDebut: PropTypes.string.isRequired,
  tempDateFin: PropTypes.string.isRequired,
  onTempDateDebutChange: PropTypes.func.isRequired,
  onTempDateFinChange: PropTypes.func.isRequired,
  onDateDebutBlur: PropTypes.func.isRequired,
  onDateFinBlur: PropTypes.func.isRequired,
  onDateDebutKeyDown: PropTypes.func.isRequired,
  onDateFinKeyDown: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onResetFilters: PropTypes.func.isRequired,
  fournisseurs: PropTypes.array.isRequired,
  clients: PropTypes.array.isRequired,
  metaux: PropTypes.array.isRequired,
  filtersRef: PropTypes.object,
  totalPrixAchat: PropTypes.number
};

export default FiltersBar;
